/**
 * "Limited to specific days & hours" — whether a rule's window is open right now.
 *
 * **A PORT of `ayeface-web-checkout/src/lib/reward-schedule.ts`, itself a port of
 * `ayeface-apk-backend/src/utils/reward-schedule.ts`, itself a mirror of
 * `reward_schedule_open(rs, tz, at)` in
 * `ayeface-merchant-portal-v2/docs/design/specs/loyalty/contracts/reward-pause-and-schedule.md`.**
 * `rewardScheduleOpen`, `isScheduleRestricted` and `formatRewardSchedule` are the web-checkout
 * original with NO logic changed, and must stay that way: apk-backend is what rejects a submit,
 * so a divergence shows the customer an enabled card that the till then refuses.
 * `rewardSchedule.test.ts` beside this file carries the backend's own case matrix for exactly
 * that reason — port a fix by porting the tests with it.
 *
 * (Quotes, line breaks and trailing commas differ from the original: this repo's eslint
 * reformats on save. Nothing else does.)
 *
 * ONE DELIBERATE DEVIATION, at the bottom: `buildScheduleSnapshot` keys on `id`, because this
 * repo's `UIReward` calls it that where the checkout's reward calls it `reward_id`. The
 * predicates above it are untouched.
 *
 * The rules, all from that contract:
 *
 * - **No row means unrestricted.** Most rules have none.
 * - `days_mask` bit 0 is **Monday** through bit 6 Sunday, matching Postgres `isodow - 1`.
 * - `time_from IS NULL` means all day on the named days.
 * - A window is **`[from, to)`** — the opening minute counts, the closing minute does not.
 * - `from > to` is an **overnight** window and **belongs to the day it starts on**: Monday
 *   22:00–02:00 runs into Tuesday morning, and it is MONDAY that has to be named.
 * - The window is wall clock in the row's own `time_zone`, captured when the rule was saved.
 *
 * Verified against the contract's own SQL, executed in Postgres over a 16-case matrix covering
 * every branch — day-only, both window edges, all four overnight positions, the Monday→Sunday
 * wrap and two zones for one instant. Every case agreed.
 *
 * **Every failure here fails OPEN.** An unreadable zone or an impossible mask means this
 * function cannot tell, and withholding a reward the merchant is advertising is the worse of
 * the two mistakes — the plan and pause gates are the ones that withhold.
 */

/** `reward_schedule`, as Sequelize returns it. `time` columns arrive as `HH:MM:SS` strings. */
export interface RewardScheduleRow {
  days_mask: number | null;
  time_from: string | null;
  time_to: string | null;
  time_zone: string | null;
}

/** Minutes since local midnight, or null when the value is not a `HH:MM[:SS]` clock. */
function minutesOfDay(clock: string | null | undefined): number | null {
  if (!clock) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(clock);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;

  return hours * 60 + minutes;
}

/** ISO day of week (1 = Monday … 7 = Sunday) and minutes since midnight, in `timeZone`. */
function localFields(
  at: Date,
  timeZone: string
): { isoDow: number; minutes: number } | null {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(at);
  } catch {
    // An unknown IANA name. Cannot place the instant, so the caller allows.
    return null;
  }

  const field = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value);
  const year = field('year');
  const month = field('month');
  const day = field('day');
  const minute = field('minute');
  // Some ICU builds emit hour 24 for midnight under hour12:false.
  const hour = field('hour') % 24;

  if ([year, month, day, hour, minute].some(Number.isNaN)) return null;

  // Build the local calendar date as a UTC instant purely to read its weekday.
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay(); // 0 = Sunday
  return { isoDow: weekday === 0 ? 7 : weekday, minutes: hour * 60 + minute };
}

/** Is `isoDow` (1–7) named in the mask? Bit 0 is Monday, so the shift is `isoDow - 1`. */
function dayNamed(mask: number, isoDow: number): boolean {
  return (mask & (1 << (isoDow - 1))) !== 0;
}

/** The ISO day before `isoDow`, wrapping Monday back to Sunday. */
function previousDay(isoDow: number): number {
  return isoDow === 1 ? 7 : isoDow - 1;
}

/**
 * True when the rule may be offered and applied at `at`.
 *
 * `at` is a real instant; the row's `time_zone` decides which wall clock it is read against.
 */
export function rewardScheduleOpen(
  schedule: RewardScheduleRow | null | undefined,
  at: Date
): boolean {
  if (!schedule) return true;

  const mask = Number(schedule.days_mask);
  // A CHECK keeps this between 1 and 127. A row outside it cannot be honoured, so it is ignored.
  if (!Number.isInteger(mask) || mask <= 0) return true;

  const local = localFields(at, schedule.time_zone ?? '');
  if (!local) return true;

  const from = minutesOfDay(schedule.time_from);
  const to = minutesOfDay(schedule.time_to);

  // All day on the named days — the stored state when the merchant picks days but no hours, and
  // the safe reading of a half-written window.
  if (from === null || to === null || from === to) {
    return dayNamed(mask, local.isoDow);
  }

  if (from < to) {
    return (
      dayNamed(mask, local.isoDow) &&
      local.minutes >= from &&
      local.minutes < to
    );
  }

  // Overnight. The evening half belongs to today; the small hours belong to YESTERDAY's window,
  // so it is yesterday that has to be named.
  return (
    (local.minutes >= from && dayNamed(mask, local.isoDow)) ||
    (local.minutes < to && dayNamed(mask, previousDay(local.isoDow)))
  );
}

/**
 * Does this row actually restrict anything?
 *
 * Three rows mean "no restriction", and only one of them is null:
 *
 * - **no row at all** — a v1-authored rule with no window;
 * - **v2's zone-holder row** — `merchant-portal-v2` writes a schedule for EVERY rule it
 *   publishes, falling back to all seven days with no hours, because the row is the only place
 *   a rule's time zone lives. Treating that as a restriction prints "Mon–Sun" on nearly every
 *   reward in the list;
 * - **an unusable mask** — `rewardScheduleOpen` reads it as unrestricted and answers true, so
 *   the UI must agree rather than advertising hours that are never enforced.
 *
 * Every caller gates on this before formatting or judging a window.
 */
export function isScheduleRestricted(
  schedule: RewardScheduleRow | null | undefined
): boolean {
  if (!schedule) return false;

  const mask = Number(schedule.days_mask);
  // KNOWN, TOLERATED DIVERGENCE (days_mask 128–255): this function reads an out-of-range mask
  // as unrestricted (returns false here). `rewardScheduleOpen` below does NOT guard the upper
  // bound — it only checks `mask <= 0` — so for the same row it falls through to `dayNamed`,
  // whose bit test only ever inspects bits 0–6 and therefore never matches bit 7+. That means
  // `rewardScheduleOpen` reads mask 128 as CLOSED on every day, while this function reads it as
  // unrestricted: the two disagree for 128–255.
  //
  // This is unreachable in practice: `aye_face_0516.reward_schedule` has
  // `CONSTRAINT reward_schedule_days_mask_check CHECK ((days_mask >= 1) AND (days_mask <= 127))`,
  // so a mask outside 1–127 cannot be stored.
  //
  // It is deliberately NOT fixed here. `rewardScheduleOpen` is required to be byte-identical to
  // apk-backend's mirror of the same name (see the file header) — adding an upper-bound guard
  // only on the frontend would make the two implementations disagree by construction, which is
  // the exact bug this port exists to prevent. And apk-backend's version can't be "fixed"
  // either: it was verified against the contract's own Postgres SQL, which has this same
  // no-upper-bound behaviour and relies on the CHECK constraint instead — changing it would
  // break the JS↔SQL mirror it exists to preserve. So: an unreachable divergence is tolerated
  // over reintroducing a reachable one. See `describe("known, deliberate divergence …")` in
  // reward-schedule.test.ts, which pins both functions' actual behaviour at days_mask: 128.
  if (!Number.isInteger(mask) || mask <= 0 || mask > 127) return false;

  const hasHours = schedule.time_from != null && schedule.time_to != null;
  return mask !== 127 || hasHours;
}

/* ───────────────────────── display, in the customer's zone ───────────────────────── */

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const MINUTES_PER_DAY = 1440;
const MINUTES_PER_WEEK = MINUTES_PER_DAY * 7;

/** `zone`'s offset from UTC in minutes at `at`, positive east. `null` when the zone is unknown. */
function zoneOffsetMinutes(at: Date, zone: string): number | null {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).formatToParts(at);
  } catch {
    return null;
  }

  const field = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value);
  const year = field('year');
  const month = field('month');
  const day = field('day');
  // Some ICU builds emit hour 24 for midnight under hour12:false.
  const hour = field('hour') % 24;
  const minute = field('minute');
  const second = field('second');
  if ([year, month, day, hour, minute, second].some(Number.isNaN)) return null;

  const localAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  return Math.round((localAsUtc - at.getTime()) / 60_000);
}

/** One occurrence, as minutes since Monday 00:00 in some zone. `end` is exclusive. */
interface WeekInterval {
  start: number;
  end: number;
}

/**
 * The rule's occurrences for one week, in the RULE's zone.
 *
 * An overnight window runs past its own day's 1440 boundary on purpose — it belongs to the day
 * it STARTS on, and keeping it as one interval is what lets the shift below move it whole.
 *
 * The all-day branch (`from === null || to === null || from === to`) is unreachable from
 * `formatRewardSchedule` in practice, but not by accident: its early-return guard tests the
 * exact same three conditions, via the same `minutesOfDay` parse, before this function is ever
 * called — so any row that would land here has already been answered without a zone shift. It
 * is kept so this function stays correct on its own terms (and matches `rewardScheduleOpen`'s
 * day-only branch, which uses this same definition of "all day").
 */
function weekIntervals(schedule: RewardScheduleRow): WeekInterval[] {
  const mask = Number(schedule.days_mask);
  const from = minutesOfDay(schedule.time_from);
  const to = minutesOfDay(schedule.time_to);
  const allDay = from === null || to === null || from === to;

  const out: WeekInterval[] = [];
  for (let day = 0; day < 7; day++) {
    if ((mask & (1 << day)) === 0) continue;
    const base = day * MINUTES_PER_DAY;

    if (allDay) out.push({ start: base, end: base + MINUTES_PER_DAY });
    else if (from < to) out.push({ start: base + from, end: base + to });
    else out.push({ start: base + from, end: base + MINUTES_PER_DAY + to });
  }
  return out;
}

/** `minutes` since local midnight as a 12-hour clock: 540 → "9:00 AM". */
function clockLabel(minutes: number): string {
  const wrapped =
    ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hour24 = Math.floor(wrapped / 60);
  const minute = wrapped % 60;
  const suffix = hour24 < 12 ? 'AM' : 'PM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${minute.toString().padStart(2, '0')} ${suffix}`;
}

/** "Mon–Fri" for a run of three or more; "Sat, Sun" for anything shorter. */
function dayRangeLabel(days: number[]): string {
  const sorted = [...days].sort((a, b) => a - b);
  const runs: string[] = [];

  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j++;
    runs.push(
      j - i >= 2
        ? `${DAY_LABELS[sorted[i]]}–${DAY_LABELS[sorted[j]]}`
        : sorted
            .slice(i, j + 1)
            .map((d) => DAY_LABELS[d])
            .join(', ')
    );
    i = j + 1;
  }
  return runs.join(', ');
}

/**
 * The window, written in the customer's own zone: `"Tue–Sat, 9:00 AM – 5:00 PM"`.
 *
 * `null` when there is nothing to say — no restriction, or a zone this runtime cannot place.
 *
 * **A weekly window does not shift like a clock.** Moving 02:00–06:00 Asia/Kuala_Lumpur into
 * UTC moves it to the PREVIOUS day; moving an overnight 22:00–02:00 window west can stop it
 * being overnight at all; a Monday window can land on the previous Sunday, wrapping the week.
 * So each occurrence becomes an interval in minutes-since-Monday, is shifted whole, wrapped
 * modulo the week, and only then read back as a day and a clock.
 *
 * **DST caveat:** the offset delta is taken at `at`. Across a transition — in the rule's zone
 * or the viewer's — a displayed time can be an hour off for occurrences on the far side of it.
 * Deliberately not solved: the alternative is rendering a different label per week.
 */
export function formatRewardSchedule(
  schedule: RewardScheduleRow | null | undefined,
  viewerZone: string,
  at: Date
): string | null {
  if (!isScheduleRestricted(schedule) || !schedule) return null;

  /*
    A days-only window is NOT zone-shifted.

    "All day Tuesday" is a claim about the merchant's Tuesday, not about a 24-hour interval that
    can be slid into another clock. Running it through the shift below would render
    "Mon, 4:00 PM – 4:00 PM" for a viewer eight hours west — technically the same instants, and
    worse in every way that matters to someone reading a reward card.

    "All day" is not just `time_from == null`: equal, non-null clocks (`time_from === time_to`)
    mean the same thing — `weekIntervals` and `rewardScheduleOpen` both treat `from === to` as
    all-day too, via this same `minutesOfDay` parse. Missing that case here let a row like
    `{ time_from: "09:00:00", time_to: "09:00:00" }` fall into the zone-shift path below, where
    `weekIntervals` built a full 24h interval that the shift then mangled into a wrong day and a
    zero-length clock range (observed: "Mon, 12:00 PM – 12:00 PM" for a Tuesday, KL→America/New_York).
    The live DB's CHECK constraint currently forbids storing equal times, so this was unreachable
    from real data — fixed anyway, since the two predicates disagreeing about "all day" is a trap
    for the next reader regardless of what the DB currently allows.
  */
  const from = minutesOfDay(schedule.time_from);
  const to = minutesOfDay(schedule.time_to);
  if (from === null || to === null || from === to) {
    const mask = Number(schedule.days_mask);
    const days = [0, 1, 2, 3, 4, 5, 6].filter(
      (day) => (mask & (1 << day)) !== 0
    );
    return days.length > 0 ? dayRangeLabel(days) : null;
  }

  const ruleOffset = zoneOffsetMinutes(at, schedule.time_zone ?? '');
  const viewerOffset = zoneOffsetMinutes(at, viewerZone);
  if (ruleOffset === null || viewerOffset === null) return null;

  const delta = viewerOffset - ruleOffset;
  const intervals = weekIntervals(schedule);
  if (intervals.length === 0) return null;

  // Shift whole, wrap into the week, then read back as (day, from, to). `to` may exceed 1440,
  // which is exactly how an overnight window survives the trip.
  //
  // For a single row this grouping always collapses to exactly one clause: a row has one shared
  // (from, to) applied uniformly across its masked days, `day * MINUTES_PER_DAY` is always a
  // multiple of MINUTES_PER_DAY, and MINUTES_PER_WEEK (10080) is itself an exact multiple of
  // MINUTES_PER_DAY (1440) — so `(day * MINUTES_PER_DAY) % MINUTES_PER_DAY` vanishes both before
  // and after the mod-10080 wrap, and every masked day's `start % MINUTES_PER_DAY` (i.e. `from`)
  // comes out identical regardless of which day it started on or what `delta` was. The grouping
  // is not dead code, though: it is what makes that collapse a computed fact rather than an
  // assumption, and it is the seam Task 7's multi-row snapshot could reuse if that ever needs to
  // join clauses from more than one schedule row into one string.
  const byWindow = new Map<
    string,
    { day: number; from: number; to: number }[]
  >();
  for (const interval of intervals) {
    const length = interval.end - interval.start;
    const start =
      (((interval.start + delta) % MINUTES_PER_WEEK) + MINUTES_PER_WEEK) %
      MINUTES_PER_WEEK;
    const day = Math.floor(start / MINUTES_PER_DAY);
    const from = start % MINUTES_PER_DAY;
    const occurrence = { day, from, to: from + length };

    const key = `${from}-${occurrence.to}`;
    const bucket = byWindow.get(key);
    if (bucket) bucket.push(occurrence);
    else byWindow.set(key, [occurrence]);
  }

  // One clause per distinct clock window, so a shift that splits the week still reads cleanly.
  const clauses = [...byWindow.values()]
    .sort(
      (a, b) =>
        a[0].from - b[0].from ||
        Math.min(...a.map((o) => o.day)) - Math.min(...b.map((o) => o.day))
    )
    .map((occurrences) => {
      const days = dayRangeLabel(occurrences.map((o) => o.day));
      const { from, to } = occurrences[0];
      const isAllDay = from === 0 && to === MINUTES_PER_DAY;
      return isAllDay
        ? days
        : `${days}, ${clockLabel(from)} – ${clockLabel(to)}`;
    });

  return clauses.join('; ');
}

/* ───────────────────────── the fetch-time snapshot ───────────────────────── */

/** What one restricted reward's window says, as of the moment the list was fetched. */
export interface ScheduleVerdict {
  open: boolean;
  label: string;
}

/** Keyed by the reward's `id`. **Absent means unrestricted** — no row, or v2's zone-holder row. */
export type ScheduleSnapshot = Map<string, ScheduleVerdict>;

/**
 * Judge and describe every restricted reward, once, at `at`.
 *
 * Built when a response arrives and then held, NOT recomputed per render. Two reasons:
 *
 * - apk-backend forgives five minutes of drift at submit, so a card that flipped to disabled
 *   the instant a customer nudged a quantity at 12:01 would be stricter than the server and
 *   would take away a selection the server would have honoured;
 * - it keeps `checkRewardIsDisabled` a pure function of its arguments, with no clock read
 *   hidden inside a `useMemo` that only claims to depend on amount and selection.
 *
 * A reward whose label cannot be built is left OUT. `rewardScheduleOpen` fails open, so
 * recording `{ open: false }` with no explanation would be the one combination that both
 * withholds the reward and cannot say why.
 */
export function buildScheduleSnapshot(
  rewards: ReadonlyArray<{ id: string; schedule?: RewardScheduleRow | null }>,
  at: Date,
  viewerZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): ScheduleSnapshot {
  const snapshot: ScheduleSnapshot = new Map();

  for (const reward of rewards) {
    if (!reward?.id || snapshot.has(reward.id)) continue;
    if (!isScheduleRestricted(reward.schedule)) continue;

    const label = formatRewardSchedule(reward.schedule, viewerZone, at);
    if (label === null) continue;

    snapshot.set(reward.id, {
      open: rewardScheduleOpen(reward.schedule, at),
      label
    });
  }

  return snapshot;
}

/* ───────────────────── repo-local: applying the snapshot ─────────────────────
 *
 * Everything above this line is the shared port. What follows exists only here,
 * because this repo's rewards arrive as a flat `UIReward[]` from an API module
 * rather than through a saga.
 */

/** The `UIReward` fields this module reads and writes. Kept structural to avoid a cycle. */
interface SchedulableReward {
  id: string;
  schedule?: RewardScheduleRow | null;
  scheduleOpen?: boolean;
  scheduleLabel?: string | null;
}

/**
 * Stamp each reward with the verdict on its window, ONCE, as the list arrives.
 *
 * Called from the API layer rather than a component, which is what makes "once
 * per fetch" true rather than aspirational: apk-backend forgives five minutes
 * of drift at submit, so a component re-reading the clock would grey a card out
 * at 17:00:01 that the server would still have honoured — the app being
 * stricter than the backend, at the customer's expense.
 *
 * An unrestricted reward is left with `scheduleOpen` UNSET, not `true`. The card
 * tests `scheduleOpen === false`, and a reward this function never judged must
 * be indistinguishable from one it judged open.
 */
export function withScheduleVerdicts<T extends SchedulableReward>(
  rewards: T[] | undefined | null,
  at: Date = new Date()
): T[] {
  if (!rewards?.length) return rewards ?? [];

  const snapshot = buildScheduleSnapshot(rewards, at);
  if (snapshot.size === 0) return rewards;

  return rewards.map((reward) => {
    const verdict = snapshot.get(reward.id);
    if (!verdict) return reward;

    return {
      ...reward,
      scheduleOpen: verdict.open,
      scheduleLabel: verdict.label
    };
  });
}
